import React, { useState, useEffect } from 'react';
import { hatsAPI } from '../services/api';
import { safeNumber, formatCurrency, calculateTieredFee } from '../utils/calculations';
import ImageLightbox from './ImageLightbox';

const FeaturedItems = ({ onTradeRequest }) => {
  const [featuredItems, setFeaturedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxItemName, setLightboxItemName] = useState('');

  useEffect(() => {
    loadFeaturedItems();
  }, []);

  const loadFeaturedItems = async () => {
    try {
      setLoading(true);
      
      // Fetch featured items
      const response = await hatsAPI.getFeatured();
      
      if (response && Array.isArray(response)) {
        // Only show the first 3 items (positions 1, 2, 3)
        const displayItems = response.filter(item => item.featured_rank <= 3);
        setFeaturedItems(displayItems);
      } else {
        setFeaturedItems([]);
      }
    } catch (err) {
      console.error('Error loading featured items:', err);
      setError('Failed to load featured items');
    } finally {
      setLoading(false);
    }
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setCurrentImageIndex(0); // Reset to first image
    setShowItemModal(true);
  };

  const closeItemModal = () => {
    setShowItemModal(false);
    setSelectedItem(null);
    setCurrentImageIndex(0);
  };

  const handleTradeRequest = (item) => {
    if (onTradeRequest) {
      onTradeRequest(item);
    }
    closeItemModal();
  };

  // Image lightbox functions
  const openImageLightbox = (item, imageIndex = 0) => {
    const images = [];
    if (item.image_url) {
      images.push(item.image_url);
    }
    if (item.additional_images && item.additional_images.length > 0) {
      images.push(...item.additional_images);
    }
    
    if (images.length > 0) {
      setLightboxImages(images);
      setLightboxIndex(imageIndex);
      setLightboxItemName(item.name);
      setLightboxOpen(true);
    }
  };

  if (loading) {
    return (
      <div className="featured-items-section">
        <h2>Featured Items</h2>
        <div className="loading">Loading featured items...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="featured-items-section">
        <h2>Featured Items</h2>
        <div className="error">{error}</div>
      </div>
    );
  }

  if (featuredItems.length === 0) {
    return (
      <div className="featured-items-section">
        <h2>Featured Items</h2>
        <div className="no-items">No featured items available</div>
      </div>
    );
  }

  return (
    <div className="featured-items-section">
      <h2>Featured Items</h2>
      <div className="featured-header">
        <p>Curated by our admin team - the best purses on the platform</p>
      </div>
      <div className="hats-grid">
        {featuredItems.map((item, index) => (
          <div key={item._id} className="compact-hat-card">
            <div className="hat-image">
                <img 
                src={item.image_url || item.image} 
                alt={`${item.name} - ${item.brand} luxury handbag`}
                loading="lazy"
                onClick={() => openImageLightbox(item)}
                style={{ cursor: 'pointer' }}
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/300x200?text=Image+Not+Available';
                }}
              />
            </div>
            <div className="compact-hat-info">
              <h3>{item.name}</h3>
              <p className="compact-hat-description">{item.description}</p>
              <div className="compact-hat-details">
                <span className="hat-brand">Brand: {item.brand}{typeof item.value === 'number' ? ` (${formatCurrency(safeNumber(item.value))})` : ''}</span>
                <span className="hat-size">Size: {item.size}</span>
                <span className="hat-condition">Condition: {item.condition}</span>
              </div>
              {item.owner_closet_name && (
                <div className="hat-closet" style={{color:'#666', fontSize:'0.9rem', marginTop:4}}>
                  {item.owner_closet_name}
                </div>
              )}
                              <div className="borrow-pill" style={{marginTop:6, color:'#0a0', fontWeight:600}}>{formatCurrency(calculateTieredFee(item.value))} Borrow</div>
              <div className="compact-hat-actions">
                <button 
                  className="btn btn-primary"
                  style={{ 
                    backgroundColor: '#d4af37', 
                    color: 'white', 
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: '600'
                  }}
                  onClick={() => handleViewItem(item)}
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Item Details Modal */}
      {showItemModal && selectedItem && (
        <div className="modal-overlay">
          <div className="item-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="item-details-content">
              <span className="close" onClick={closeItemModal}>&times;</span>
              <div className="item-image">
                <img 
                  src={(() => {
                    // Prepare all images (main + additional)
                    const allImages = [];
                    if (selectedItem.image_url) {
                      allImages.push(selectedItem.image_url);
                    }
                    if (selectedItem.additional_images && selectedItem.additional_images.length > 0) {
                      allImages.push(...selectedItem.additional_images);
                    }
                    return allImages.length > 0 ? allImages[currentImageIndex] : '';
                  })()} 
                  alt={`${selectedItem.name} - ${selectedItem.brand} luxury handbag detail view`}
                  onClick={() => openImageLightbox(selectedItem)}
                  style={{ cursor: 'pointer' }} 
                />
                {/* Image thumbnails */}
                {(() => {
                  const allImages = [];
                  if (selectedItem.image_url) {
                    allImages.push(selectedItem.image_url);
                  }
                  if (selectedItem.additional_images && selectedItem.additional_images.length > 0) {
                    allImages.push(...selectedItem.additional_images);
                  }
                  return allImages.length > 1 ? (
                    <div className="image-thumbnails">
                      {allImages.map((image, index) => (
                        <div 
                          key={index}
                          className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => setCurrentImageIndex(index)}
                        >
                          <img 
                            src={image} 
                            alt={`${selectedItem.name} - ${selectedItem.brand} thumbnail ${index + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
              <div className="item-info">
                <h4>{selectedItem.name}</h4>
                <p>{selectedItem.description}</p>
                <div className="item-details">
                  <span>Brand: {selectedItem.brand}</span>
                  <span>Size: {selectedItem.size}</span>
                  <span>Condition: {selectedItem.condition}</span>
                  {selectedItem.value && <span>Value: ${selectedItem.value}</span>}
                </div>
                <div className="item-actions">
                  <button className="btn btn-primary" onClick={() => handleTradeRequest(selectedItem)}>Request Trade</button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      window.location.href = `/closet/${selectedItem.user_id}`;
                    }}
                  >
                    View Owner's Closet
                  </button>
                  <button className="btn btn-secondary" onClick={closeItemModal}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        itemName={lightboxItemName}
        onClose={() => setLightboxOpen(false)}
        onIndexChange={setLightboxIndex}
      />
    </div>
  );
};

export default FeaturedItems;

// Add CSS styles for image thumbnails in FeaturedItems modal
const styles = `
  .item-image {
    position: relative;
  }
  
  .image-thumbnails {
    display: flex;
    gap: 8px;
    margin-top: 10px;
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .thumbnail {
    width: 50px;
    height: 50px;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  
  .thumbnail:hover {
    border-color: #007bff;
    transform: scale(1.05);
  }
  
  .thumbnail.active {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  }
  
  .thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
